import { Modal as AntModal } from 'antd';
import type { ModalProps as AntModalProps } from 'antd';
import { useCallback, type ReactNode } from 'react';
import styled from 'styled-components';
import { stableScrollGutter } from '../../../assets/styles/components/scrollGutter';
import { ResizeGrip } from '../../icons';
import { PANE_MARGIN_PX, useModalResize } from './useModalResize';
import { useKeepTabInsideModal } from './useKeepTabInsideModal';

export const DEFAULT_MODAL_WIDTH = 370;

const StyledModal = styled(AntModal)`
  .ant-modal-content {
    padding: 15px;
  }
  /**
   * Opt-in through the resizable prop. Content that should grow with the dialog marks its
   * flex chain with .resizable-modal-fill and the element that scrolls with
   * .resizable-modal-scroll; once resized, the scroll element drops its own height cap.
   */
  &.resizable-modal {
    .resizable-modal-frame {
      position: relative;
    }
    .ant-modal-content {
      display: flex;
      flex-direction: column;
      /* a centred dialog has equal room above and below, so the whole box can be capped to the pane */
      max-height: calc(var(--viewport-height) - ${2 * PANE_MARGIN_PX}px);
    }
    .ant-modal-header {
      flex-shrink: 0;
    }
    .ant-modal-body,
    .resizable-modal-fill {
      display: flex;
      flex-direction: column;
      flex: 1 1 auto;
      min-height: 0;
    }
    .resizable-modal-scroll {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
    }
    &.is-resized .resizable-modal-scroll {
      max-height: none;
    }
    &.is-resizing {
      cursor: nwse-resize;
      user-select: none;
    }
    .modal-resize-handle {
      position: absolute;
      right: 0;
      bottom: 0;
      z-index: 1;
      display: flex;
      align-items: flex-end;
      justify-content: flex-end;
      width: 16px;
      height: 16px;
      padding: 2px;
      font-size: 14px;
      color: var(--clr-general-description);
      cursor: nwse-resize;
      /* antd turns pointer events off on .ant-modal and back on only for .ant-modal-content */
      pointer-events: auto;
      touch-action: none;
      user-select: none;
    }
  }
  /**
   * Opt-in for a scroll area that should end at the modal's own edge: it grows down through
   * the content padding above and takes that spacing over as its own, so the spacing scrolls
   * away with the list instead of leaving a blank band under a half-cut row. The corners are
   * clipped because the list then runs over the modal's rounded edge.
   */
  .scroll-to-modal-edge {
    margin-bottom: -15px;
    padding-bottom: 15px;
    /* the strip the scrollbar sits in comes out of the content padding, so the rows keep the
       same space either side of them */
    ${stableScrollGutter}
  }
  .ant-modal-content:has(.scroll-to-modal-edge) {
    overflow: hidden;
  }
  /* Bleeds through the content padding so the divider runs from edge to edge. */
  .closable-modal-header {
    margin: -15px -15px 15px;
    border-bottom: 1px solid var(--clr-border-grey-100);
    /* Keeps the 24px hit area without making the header taller than its 20px title row. */
    .closable-modal-close {
      margin: -2px;
    }
  }
  &.confirm-modal {
    .ant-modal-content {
      padding-top: 30px;
      .ant-modal-close {
        width: 20px;
        height: 20px;
        color: var(--clr-general-description);
        &:hover {
          background-color: transparent;
        }
      }
    }
    .icon-container {
      width: 70px;
      height: 70px;
      border-radius: 60px;
      background-color: rgba(var(--clr-general-primary-rgb), 0.1);
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 10px;
      .icon {
        font-size: 30px;
        color: var(--clr-title);
        height: 30px;
        display: inline-block;
      }
    }
  }
  &.descriptionmodal {
    /* opening below the pane height leaves room to resize vertically; .is-resized lifts the cap */
    .resizable-modal-scroll {
      max-height: 320px;
    }
  }
  /* A fixed height, so switching between the two creation tabs doesn't resize the dialog. */
  &.create-preliminary-invoice-modal {
    .ant-modal-content {
      display: flex;
      flex-direction: column;
      height: 348px;
    }
    .ant-modal-body {
      display: flex;
      flex-direction: column;
      flex: 1 1 auto;
      min-height: 0;
    }
  }
  &.quick-action-modal {
    .quick-action-group {
      min-inline-size: 0;
      margin: 0;
      padding: 0;
      border: 0;
      legend {
        padding: 0;
      }
    }
    .quick-action-group:not(:last-child) {
      padding-bottom: 10px;
      border-bottom: 1px solid var(--clr-border-grey-100);
    }

    /* antd's text button is a centred, single-line 30px box; a menu row starts at the icon and may wrap. */
    .quick-action-item {
      justify-content: flex-start;
      gap: 10px;
      height: auto;
      min-height: 40px;
      padding: 0 10px;
      border: 0;
      white-space: normal;
      text-align: start;
      .icon {
        flex-shrink: 0;
        font-size: 20px;
        color: var(--clr-general-primary);
      }
      /* Shaped like antd's own state selectors, whose text-button hover is transparent and press is grey. */
      &:not(:disabled):not(.ant-btn-disabled):hover,
      &:not(:disabled):not(.ant-btn-disabled):active,
      &:focus-visible {
        background-color: var(--clr-general-hover);
      }
      &:focus-visible {
        outline-offset: -2px;
      }
    }
  }
`;

export type ModalProps = Omit<AntModalProps, 'width' | 'panelRef'> & {
  width?: number;
  /** Adds a bottom-right handle for dialogs whose content benefits from more room, like long text or lists. */
  resizable?: boolean;
  /** Names a dialog that renders its own heading instead of antd's `title`. */
  'aria-labelledby'?: string;
};

type DialogProps = Omit<ModalProps, 'resizable' | 'aria-labelledby'> &
  Pick<AntModalProps, 'panelRef'>;

type ResizableFrameOptions = Pick<
  ReturnType<typeof useModalResize>,
  'frameRef' | 'resizeHandleProps'
> &
  Pick<AntModalProps, 'modalRender'>;

const createResizableFrameRender =
  ({ frameRef, resizeHandleProps, modalRender }: ResizableFrameOptions) =>
  (node: ReactNode) => (
    <div ref={frameRef} className="resizable-modal-frame">
      {modalRender ? modalRender(node) : node}
      <span
        className="modal-resize-handle"
        data-testid="modal-resize-handle"
        aria-hidden="true"
        {...resizeHandleProps}
      >
        <ResizeGrip />
      </span>
    </div>
  );

const ResizableModal = ({
  open = false,
  width = DEFAULT_MODAL_WIDTH,
  className,
  styles,
  modalRender,
  ...props
}: DialogProps) => {
  const { frameRef, size, isResizing, resizeHandleProps } = useModalResize({
    open,
    minWidth: width,
  });
  const resizeClassName = [
    className,
    'resizable-modal',
    size && 'is-resized',
    isResizing && 'is-resizing',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <StyledModal
      {...props}
      open={open}
      width={size?.width ?? width}
      className={resizeClassName}
      styles={{ ...styles, content: { ...styles?.content, height: size?.height } }}
      modalRender={createResizableFrameRender({ frameRef, resizeHandleProps, modalRender })}
    />
  );
};

// rc-dialog only labels its role="dialog" element from its own `title`, so the label is set on that element directly.
const useLabelDialogRef = (labelledBy: string | undefined) =>
  useCallback(
    (dialog: HTMLDivElement | null) => {
      if (labelledBy) dialog?.setAttribute('aria-labelledby', labelledBy);
    },
    [labelledBy],
  );

export const Modal = ({
  resizable = false,
  'aria-labelledby': labelledBy,
  ...props
}: ModalProps) => {
  useKeepTabInsideModal(props.open ?? false);
  const labelDialogRef = useLabelDialogRef(labelledBy);
  const modalProps: DialogProps = { ...props, panelRef: labelledBy ? labelDialogRef : undefined };
  return resizable ? <ResizableModal {...modalProps} /> : <StyledModal {...modalProps} />;
};
